<template>
  <BaseCard
    title="Office vs Agent Commission Breakdown"
    class="h-full"
  >
    <template #content>
         <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else-if="hasData"
            class="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full p-4 rounded-lg"
          >
            <!-- Pie Chart -->
            <div class="flex flex-col gap-4">
              <span class="text-lg font-bold text-gray-700">
                Office vs Agent Commission
              </span>
              <div class="bg-white border border-gray-200 p-4 rounded-lg">
                <ClientOnly>
                  <VChart
                    class="w-full min-h-[350px] max-h-[400px]"
                    :option="option"
                    autoresize
                  />
                  <template #fallback>
                    <div class="text-center text-gray-500 p-4">
                      Loading chart...
                    </div>
                  </template>
                </ClientOnly>
              </div>
            </div>

            <!-- Agents Table -->
            <div class="flex flex-col gap-4">
              <span class="text-lg font-bold text-gray-700">
                Agents Commission
              </span>
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <DataTable
                  :value="commissionByAgent"
                  data-key="agentId"
                  class="w-full"
                  responsive-layout="scroll"
                >
                  <template #empty>
                    <div class="px-4 py-8 text-center text-sm text-gray-500">
                      No agent commission data available
                    </div>
                  </template>

                  <Column
                    field="agentName"
                    header="Agent"
                  />
                  <Column
                    field="totalCommission"
                    header="Commission"
                  >
                    <template #body="slotProps">
                      {{ formatCurrency(slotProps.data.totalCommission) }}
                    </template>
                  </Column>
                  <Column
                    field="contractCount"
                    header="Contracts"
                  />
                </DataTable>
              </div>
            </div>
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center p-12 rounded-lg"
          >
            <Icon
              name="mdi:chart-donut"
              size="48"
              class="text-gray-300 mb-4"
            />
            <p class="text-gray-500 text-center">
              No commission breakdown data available
            </p>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import type { ComposeOption } from 'echarts/core'
import type {
  PieSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
} from 'echarts'
import { useDashboard } from '~/composables/useDashboardCA'
import { useUtils } from '~/composables/useUtils'

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
])

type EChartsOption = ComposeOption<
  | PieSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
>

interface CommissionByAgent {
  agentId: string
  agentName: string
  totalCommission: number
  contractCount: number
  averageCommission: number
}

interface OfficeVsAgentBreakdown {
  officeCommission: number
  agentCommission: number
  totalCommission: number
}

interface CommissionAnalyticsResponse {
  summary: unknown
  commissionByAgent: CommissionByAgent[]
  commissionByMonth: unknown[]
  commissionByStage: unknown[]
  commissionByStatus: unknown[]
  officeVsAgentBreakdown: OfficeVsAgentBreakdown
}

const { getCommissionAnalytics } = useDashboard()
const { formatToDollarsWithoutCents } = useUtils()

const isLoading = ref(true)
const officeVsAgentBreakdown = ref<OfficeVsAgentBreakdown>({
  officeCommission: 0,
  agentCommission: 0,
  totalCommission: 0,
})
const commissionByAgent = ref<CommissionByAgent[]>([])

const hasData = computed(() => {
  return officeVsAgentBreakdown.value.totalCommission > 0 || commissionByAgent.value.length > 0
})

const formatCurrency = (value: number): string => {
  if (!value || value === 0) return '$0'
  const formatted = formatToDollarsWithoutCents(value)
  return formatted.formatted || '$0'
}

const getPrimaryColor = () => {
  return getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim() || '#97BA9F'
}

const option = computed<EChartsOption>(() => {
  if (!hasData.value || officeVsAgentBreakdown.value.totalCommission === 0) {
    return {
      title: {
        text: 'No Data',
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#999',
        },
      },
    }
  }

  const primaryColor = getPrimaryColor()
  const secondaryColor = 'rgba(16, 185, 129, 1)'

  const data = [
    {
      value: officeVsAgentBreakdown.value.officeCommission,
      name: 'Office Commission',
    },
    {
      value: officeVsAgentBreakdown.value.agentCommission,
      name: 'Agent Commission',
    },
  ]

  return {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: '#fff',
      },
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { name, value, percent } = params as any
        return `${name}<br/>${formatCurrency(value)} (${percent}%)`
      },
    },
    legend: {
      orient: 'horizontal',
      top: 'bottom',
      textStyle: {
        color: '#666',
      },
    },
    series: [
      {
        name: 'Commission',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          fontSize: 12,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        labelLine: {
          show: true,
        },
        data,
        color: [primaryColor, secondaryColor],
      },
    ],
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getCommissionAnalytics()
    const data = response?.data as CommissionAnalyticsResponse

    if (data) {
      officeVsAgentBreakdown.value = data.officeVsAgentBreakdown || {
        officeCommission: 0,
        agentCommission: 0,
        totalCommission: 0,
      }
      commissionByAgent.value = data.commissionByAgent || []
    }
  }
  catch (error) {
    console.error('Error fetching commission analytics:', error)
    officeVsAgentBreakdown.value = {
      officeCommission: 0,
      agentCommission: 0,
      totalCommission: 0,
    }
    commissionByAgent.value = []
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
